import React, { useState, useEffect } from "react";
import { Box, Typography, TextField, Button, Card, CardContent, Grid, 
  FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
  Snackbar, Alert, CircularProgress, Divider, Chip } from "@mui/material";
import { AccountBalance, Refresh, TrendingUp, TrendingDown } from "@mui/icons-material";
import axios from "axios";
import { api_urls } from "../../utils/api-urls";

const TBOSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  
  // Flight Markup
  const [flightMarkupType, setFlightMarkupType] = useState("fixed");
  const [flightMarkupValue, setFlightMarkupValue] = useState(0);
  const [flightMarkupActive, setFlightMarkupActive] = useState(true);
  
  // Hotel Markup
  const [hotelMarkupType, setHotelMarkupType] = useState("fixed");
  const [hotelMarkupValue, setHotelMarkupValue] = useState(0);
  const [hotelMarkupActive, setHotelMarkupActive] = useState(true);

  // TBO Wallet Balance
  const [walletBalance, setWalletBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchTBOBalance();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${api_urls}api/tbo/settings`);
      if (response.data?.success && response.data?.settings) {
        const { flight, hotel } = response.data.settings;
        setFlightMarkupType(flight.type || "fixed");
        setFlightMarkupValue(flight.value || 0);
        setFlightMarkupActive(flight.active !== false);
        setHotelMarkupType(hotel.type || "fixed");
        setHotelMarkupValue(hotel.value || 0);
        setHotelMarkupActive(hotel.active !== false);
      }
    } catch (error) {
      console.error("Error fetching TBO settings:", error);
      setSnackbar({ open: true, message: "Failed to load settings", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const fetchTBOBalance = async () => {
    try {
      setLoadingBalance(true);
      const response = await axios.get(`${api_urls}api/tbo/admin/balance`);
      if (response.data?.success && response.data?.balance) {
        setWalletBalance(response.data.balance);
      }
    } catch (error) {
      console.error("Error fetching TBO balance:", error);
    } finally {
      setLoadingBalance(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const adminId = localStorage.getItem("userId");
      
      const response = await axios.post(`${api_urls}api/tbo/settings/update`, {
        flightMarkupType,
        flightMarkupValue: Number(flightMarkupValue),
        flightMarkupActive,
        hotelMarkupType,
        hotelMarkupValue: Number(hotelMarkupValue),
        hotelMarkupActive,
        adminId
      });

      if (response.data?.success) {
        setSnackbar({ open: true, message: "Settings saved successfully!", severity: "success" });
      } else {
        setSnackbar({ open: true, message: "Failed to save settings", severity: "error" });
      }
    } catch (error) {
      console.error("Error saving TBO settings:", error);
      setSnackbar({ open: true, message: "Error saving settings", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom fontWeight="bold" color="primary">
        TBO Settings
      </Typography>

      {/* TBO Wallet Balance Card */}
      <Card elevation={4} sx={{ mb: 3, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" color="white" gutterBottom>
                <AccountBalance sx={{ mr: 1, verticalAlign: "bottom" }} />
                TBO Agency Wallet
              </Typography>
              {loadingBalance ? (
                <CircularProgress size={30} sx={{ color: "white" }} />
              ) : walletBalance ? (
                <>
                  <Typography variant="h3" color="white" fontWeight="bold">
                    {formatCurrency(walletBalance.availableLimit)}
                  </Typography>
                  <Box display="flex" gap={2} mt={1}>
                    <Chip 
                      icon={<TrendingUp sx={{ color: "white !important" }} />}
                      label={`Credit Limit: ${formatCurrency(walletBalance.creditLimit)}`}
                      sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
                    />
                    <Chip 
                      icon={<TrendingDown sx={{ color: "white !important" }} />}
                      label={`Used: ${formatCurrency(walletBalance.creditLimit - walletBalance.availableLimit)}`}
                      sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
                    />
                  </Box>
                </>
              ) : (
                <Typography variant="body1" color="white">
                  Unable to fetch balance
                </Typography>
              )}
            </Box>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={fetchTBOBalance}
              disabled={loadingBalance}
              sx={{ bgcolor: "rgba(255,255,255,0.2)", "&:hover": { bgcolor: "rgba(255,255,255,0.3)" } }}
            >
              Refresh
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h5" gutterBottom fontWeight="bold" color="primary">
        Markup Configuration
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={3}>
        Configure markup/commission for flight and hotel bookings. This amount will be added to the displayed price.
      </Typography>

      <Grid container spacing={3}>
        {/* Flight Markup Card */}
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                ✈️ Flight Markup
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={flightMarkupActive}
                    onChange={(e) => setFlightMarkupActive(e.target.checked)}
                    color="primary"
                  />
                }
                label={flightMarkupActive ? "Active" : "Inactive"}
                sx={{ mb: 2 }}
              />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Markup Type</InputLabel>
                <Select
                  value={flightMarkupType}
                  label="Markup Type"
                  onChange={(e) => setFlightMarkupType(e.target.value)}
                >
                  <MenuItem value="fixed">Fixed Amount (₹)</MenuItem>
                  <MenuItem value="percentage">Percentage (%)</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label={flightMarkupType === "fixed" ? "Markup Amount (₹)" : "Markup Percentage (%)"}
                type="number"
                value={flightMarkupValue}
                onChange={(e) => setFlightMarkupValue(e.target.value)}
                InputProps={{ inputProps: { min: 0 } }}
              />

              <Box mt={2} p={2} bgcolor="#f5f5f5" borderRadius={1}>
                <Typography variant="body2" color="textSecondary">
                  <strong>Example:</strong><br />
                  Base Fare: ₹5,000<br />
                  {flightMarkupType === "fixed" 
                    ? `Markup: ₹${flightMarkupValue}` 
                    : `Markup (${flightMarkupValue}%): ₹${Math.round(5000 * flightMarkupValue / 100)}`}<br />
                  <strong>Total: ₹{flightMarkupType === "fixed" 
                    ? 5000 + Number(flightMarkupValue) 
                    : 5000 + Math.round(5000 * flightMarkupValue / 100)}</strong>
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Hotel Markup Card */}
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                🏨 Hotel Markup
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={hotelMarkupActive}
                    onChange={(e) => setHotelMarkupActive(e.target.checked)}
                    color="primary"
                  />
                }
                label={hotelMarkupActive ? "Active" : "Inactive"}
                sx={{ mb: 2 }}
              />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Markup Type</InputLabel>
                <Select
                  value={hotelMarkupType}
                  label="Markup Type"
                  onChange={(e) => setHotelMarkupType(e.target.value)}
                >
                  <MenuItem value="fixed">Fixed Amount (₹)</MenuItem>
                  <MenuItem value="percentage">Percentage (%)</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label={hotelMarkupType === "fixed" ? "Markup Amount (₹)" : "Markup Percentage (%)"}
                type="number"
                value={hotelMarkupValue}
                onChange={(e) => setHotelMarkupValue(e.target.value)}
                InputProps={{ inputProps: { min: 0 } }}
              />

              <Box mt={2} p={2} bgcolor="#f5f5f5" borderRadius={1}>
                <Typography variant="body2" color="textSecondary">
                  <strong>Example:</strong><br />
                  Room Rate: ₹3,000/night<br />
                  {hotelMarkupType === "fixed" 
                    ? `Markup: ₹${hotelMarkupValue}` 
                    : `Markup (${hotelMarkupValue}%): ₹${Math.round(3000 * hotelMarkupValue / 100)}`}<br />
                  <strong>Total: ₹{hotelMarkupType === "fixed" 
                    ? 3000 + Number(hotelMarkupValue) 
                    : 3000 + Math.round(3000 * hotelMarkupValue / 100)}</strong>
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box mt={3} display="flex" justifyContent="center">
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSave}
          disabled={saving}
          sx={{ minWidth: 200 }}
        >
          {saving ? <CircularProgress size={24} color="inherit" /> : "Save Settings"}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TBOSettings;
